import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Activity, Clock, Zap, Server, ExternalLink } from "lucide-react";
import { getProviderApis, getProviders } from "../services/providerService";
import { getIntegrations } from "../services/integrationService";
import { useProviderLogo } from "../hooks/useProviderLogo";
import IntegrationTopBar from "./IntegrationTopBar";
import StatCard from "./integration/StatCard";
import ServiceCard from "./integration/ServiceCard";
import ActivityItem from "./integration/ActivityItem";
import ConfigurationModal from "./integration/ConfigurationModal";

export default function IntegrationDetail() {
  const { integrationId } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("services");
  const [configuringService, setConfiguringService] = useState(null);
  const [existingIntegration, setExistingIntegration] = useState(null);
  const [providerApis, setProviderApis] = useState([]);
  const [apisLoading, setApisLoading] = useState(true);
  // Fallback provider resolved by slug/id for deep-links & page refresh
  // (when there's no navigation state from the Dashboard).
  const [fetchedProvider, setFetchedProvider] = useState(null);

  // Provider context normally comes from navigation state (Dashboard click);
  // on a direct URL / refresh it's absent, so fall back to the fetched provider.
  const providerData = location.state?.providerData || fetchedProvider;
  const providerId = location.state?.providerId || fetchedProvider?.id;

  // Fetch provider logo using custom hook
  const logoUrl = useProviderLogo(providerId);

  // Use provider data directly or create minimal fallback
  const integration = providerData
    ? { ...providerData, iconPath: logoUrl || providerData.iconPath }
    : {
        id: integrationId,
        slug: integrationId,
        name: integrationId
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        description: "API Integration",
        iconPath: logoUrl || "/logo.svg",
        status: "Available",
        category: "Integration"
      };

  // Helper: Calculate average latency from service APIs (divide by total count)
  const calculateAvgLatency = (apis) => {
    if (apis.length === 0) return "N/A";
    const totalLatency = apis.reduce(
      (sum, api) => sum + (api.avg_latency || 0),
      0
    );
    const avg = totalLatency / apis.length;
    return `${Math.round(avg)}ms`;
  };

  // Helper: Calculate average total requests (divide by total count)
  const calculateAvgRequests = (apis) => {
    if (apis.length === 0) return "N/A";
    const totalRequests = apis.reduce(
      (sum, api) => sum + (api.total_requests || 0),
      0
    );
    const avg = totalRequests / apis.length;
    return Math.round(avg).toLocaleString();
  };

  // Computed stats using provider-level data and aggregated service data
  const stats = {
    totalRequests:
      providerApis.length > 0 ? calculateAvgRequests(providerApis) : "N/A",
    uptime:
      providerData?.uptime_percentage !== null &&
      providerData?.uptime_percentage !== undefined
        ? `${providerData.uptime_percentage}%`
        : "N/A",
    avgLatency:
      providerApis.length > 0 ? calculateAvgLatency(providerApis) : "N/A",
    services: providerApis.length || "N/A"
  };

  const isConnected = integration.status === "Connected";

  // Deep-link / refresh fallback: when there's no navigation state, resolve the
  // provider from the URL param (matches by id OR slug) so the title, stats and
  // services load instead of showing the raw id + "No services available".
  useEffect(() => {
    if (location.state?.providerData || !integrationId) return;
    let cancelled = false;
    (async () => {
      const res = await getProviders({ limit: 200 });
      if (cancelled || !res.success || !Array.isArray(res.data)) return;
      const slugify = (s) => (s || "").toLowerCase().replace(/\s+/g, "_");
      const match = res.data.find(
        (p) =>
          String(p.provider_id) === integrationId ||
          slugify(p.ui_name || p.provider_name) === integrationId
      );
      if (match) {
        // Mirror Dashboard.jsx's provider->integration mapping so the title,
        // stats and services render identically to the click-through path.
        setFetchedProvider({
          id: match.provider_id,
          slug: integrationId,
          name: match.ui_name || match.provider_name,
          description: match.description || `${match.provider_name} integration`,
          status: match.status || "Available",
          category: match.provider_type,
          uptime_percentage: match.uptime_percentage,
          total_requests: match.total_requests,
          successful_requests: match.successful_requests
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [integrationId, location.state]);

  // Fetch provider APIs when component mounts
  useEffect(() => {
    const fetchApis = async () => {
      try {
        setApisLoading(true);
        // Use providerId from navigation state
        const response = await getProviderApis(providerId);

        if (response.success && response.data) {
          setProviderApis(response.data);
        } else {
          console.warn("⚠️ Could not load provider APIs");
        }
      } catch (error) {
        console.error("Error loading provider APIs:", error);
      } finally {
        setApisLoading(false);
      }
    };

    if (providerId) {
      fetchApis();
    } else {
      setApisLoading(false);
    }
  }, [providerId]);

  // Handle Configure button click - fetch existing integration if it exists
  const handleConfigure = async (service) => {
    try {
      // Fetch existing integration for this provider and API
      const response = await getIntegrations({
        provider_id: providerId,
        provider_api_id: service.api_id // Use the actual API ID from the service
      });

      if (response.success && response.data && response.data.length > 0) {
        // Integration exists - pass it to modal for editing
        setExistingIntegration(response.data[0]);
      } else {
        // No existing integration - will create new one
        setExistingIntegration(null);
      }

      // Open modal with service data
      setConfiguringService(service);
    } catch (error) {
      console.error("Error fetching existing integration:", error);
      // Open modal anyway, will create new integration
      setExistingIntegration(null);
      setConfiguringService(service);
    }
  };

  const tabs = [
    { id: "services", label: "Services & APIs" },
    { id: "activity", label: "Activity Log" },
    { id: "overview", label: "Overview" }
  ];

  const statCards = [
    {
      title: "Total Requests",
      value: stats.totalRequests,
      subtext: "Last 24 hours",
      icon: Activity,
      iconColor: "text-blue-600"
    },
    {
      title: "Uptime",
      value: stats.uptime,
      subtext: "All systems operational",
      icon: Zap,
      iconColor: "text-green-600"
    },
    {
      title: "Avg Latency",
      value: stats.avgLatency,
      subtext: "Response time",
      icon: Clock,
      iconColor: "text-blue-600"
    },
    {
      title: "Services",
      value: stats.services,
      subtext: "APIs connected",
      icon: Server,
      iconColor: "text-purple-600"
    }
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Integration Top Bar */}
      <IntegrationTopBar integration={integration} />

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-[#FCFCFC]">
        <div className="max-w-[1400px] mx-auto p-8">
          {/* Stats Cards */}
          {isConnected && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statCards.map((stat, index) => (
                <StatCard key={index} {...stat} />
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-6 mb-6 border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 font-medium text-sm transition-colors relative ${
                  activeTab === tab.id
                    ? "text-gray-900"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"></div>
                )}
              </button>
            ))}
          </div>

          {/* Services & APIs Tab */}
          {activeTab === "services" && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Available Services
                </h2>
                <p className="text-gray-600">
                  Select and configure the APIs you want to use from{" "}
                  {integration.name}
                </p>
              </div>

              {apisLoading ? (
                // Loading skeleton
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-xl p-6 animate-pulse"
                    >
                      <div className="h-6 w-48 bg-gray-200 rounded mb-3"></div>
                      <div className="h-4 w-full bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : providerApis.length > 0 ? (
                // Real API data from /api/v1/metrics/providers/{provider_id}/apis
                <div className="space-y-4">
                  {providerApis.map((api, index) => (
                    <ServiceCard
                      key={index}
                      service={{
                        id: index,
                        api_id: api.provider_api_id, // provider_api_id from API response
                        provider_id: api.provider_id, // provider_id from API response
                        auth_type: api.auth_type || "api_key", // legacy single value
                        supported_auth_types: api.supported_auth_types || [
                          "api_key"
                        ], // methods this API supports (drives the selector)
                        // Credential keys this API's auth handler reads, in
                        // display order. Drives the OAuth form's fields, so a
                        // provider needing more than client_id/client_secret
                        // (e.g. a password grant) can actually be configured.
                        credential_fields: api.credential_fields || null,
                        name: api.name,
                        description:
                          api.description || "No description available",
                        status: api.status || "Available",
                        endpoints: api.total_endpoints || 0,
                        requests: api.total_requests || 0,
                        latency: api.avg_latency
                          ? `${api.avg_latency}ms`
                          : "N/A",
                        enabled: api.status === "Connected" // Enable if connected
                      }}
                      integration={integration}
                      onConfigure={handleConfigure}
                    />
                  ))}
                </div>
              ) : (
                // Empty state - No services from API
                <div className="text-center py-12">
                  <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    No services available for this integration
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Activity Log Tab */}
          {activeTab === "activity" && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Recent Activity
                </h2>
                <p className="text-gray-600">
                  API requests and system events for this integration
                </p>
              </div>
              {providerApis.length > 0 ? (
                <div className="space-y-4">
                  {providerApis.map((activity) => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No recent activity to display</p>
                </div>
              )}
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8">
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  About {integration.name}
                </h2>
                <p className="text-gray-600 text-base leading-relaxed">
                  {integration.description}
                </p>
              </div>
              <div className="border-t border-gray-200 pt-8">
                <div className="grid grid-cols-1 gap-8">
                  {/* Provider */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      Provider
                    </h3>
                    <p className="text-gray-600">
                      {integration.subtitle || integration.name}
                    </p>
                  </div>
                  {/* Category */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Category
                    </h3>
                    <span className="inline-block px-4 py-2 bg-gray-100 text-gray-900 rounded-lg text-sm font-medium">
                      {integration.category}
                    </span>
                  </div>
                  {/* Documentation */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Documentation
                    </h3>
                    {integration.documentationUrl ||
                    providerData?.documentation_url ? (
                      <button
                        onClick={() =>
                          window.open(
                            integration.documentationUrl ||
                              providerData.documentation_url,
                            "_blank"
                          )
                        }
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View API Documentation
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    ) : (
                      <p className="text-gray-500 text-sm">
                        No documentation available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Modal */}
      {configuringService && (
        <ConfigurationModal
          service={configuringService}
          integration={integration}
          providerId={providerId}
          existingIntegration={existingIntegration}
          supportedAuthTypes={
            configuringService.supported_auth_types || ["api_key"]
          }
          supportedAuthType={configuringService.auth_type || "api_key"}
          onClose={(success) => {
            setConfiguringService(null);
            setExistingIntegration(null);
            // Refresh provider APIs if integration was successful
            if (success && providerId) {
              getProviderApis(providerId).then((response) => {
                if (response.success && response.data) {
                  setProviderApis(response.data);
                }
              });
            }
          }}
        />
      )}
    </div>
  );
}
